import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject, Input,
  OnInit,
  Output,
  ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { range } from 'lodash-es';
import { tap } from 'rxjs/operators';


import { IModalService, MODAL_SERVICE_TOKEN } from '../../../services/IModalService';
import { IMusicService, MUSIC_SERVICE_TOKEN } from '../../../services/IMusicService';
import { IQuestionEventsService, QUESTION_EVENTS_SERVICE_TOKEN } from '../../../services/IQuestionEvents';
import { IStudentMusicService, STUDENT_MUSIC_SERVICE_TOKEN } from '../../../services/IStudentMusicService';
import { ManipControlsAtBottomComponent } from '../../modals';
import { injectManipControlBarVisibility } from '../../services/manip-control-bar-scope';

@UntilDestroy()
@Component({
  selector: 'kh-integer-input',
  styleUrls: ['./integer-input.component.less'],
  templateUrl: './integer-input.component.html',
  standalone: false
})
export class IntegerInputComponent implements OnInit, AfterViewInit {
  public questionClosed = false;
  public isClosedByTeacher = false;
  public initValue = false;
  public isManipChanged = false;

  @Input() public hideInput = false;
  @Input() public upLabel?: string;
  @Input() public downLabel?: string;
  @Input() public values = '';
  @Input() public itemIndex = 0;
  @Input() public defaultValue?: string;
  @Input() public reverseButtonOrder = false;
  @Input() public minValue = 0;
  @Input() public maxValue = 10;
  @Input() public disabledInput = false;

  @Output() public submitAnswer = new EventEmitter<string>();
  @Output() public valueChange = new EventEmitter<string>();
  @Output() public itemIndexChange = new EventEmitter<number>();
  @ViewChild('add') public add!: ElementRef;
  @ViewChild('substract') public substract!: ElementRef;
  @ViewChild('reverse') public reverse!: ElementRef;

  private val = '';
  private valuesList: string[] = [];

  private readonly controlBarVisibility = injectManipControlBarVisibility();
  public hideStudentManipControlBar = this.controlBarVisibility.hidden$;

  public constructor(
    @Inject(QUESTION_EVENTS_SERVICE_TOKEN) private readonly questionEventsService: IQuestionEventsService,
    @Inject(MODAL_SERVICE_TOKEN) private readonly modalService: IModalService,
    @Inject(STUDENT_MUSIC_SERVICE_TOKEN) private readonly studentMusicService: IStudentMusicService,
    @Inject(MUSIC_SERVICE_TOKEN) private readonly sharedMusicService: IMusicService,
    private readonly route: ActivatedRoute) {
  }

  public get inputValue(): string {
    return this.val !== '' ? this.val : '0';
  }

  @Input()
  public set value(value: string) {
    this.val = value ? value : '';
    this.updateItemIndex(this.val);
  }

  public get upIsPlus(): boolean {
    return this.upLabel === '+';
  }

  public get upIsNotPlus(): boolean {
    return Boolean(this.upLabel) && this.upLabel !== '+';
  }

  public get downIsMinus(): boolean {
    return this.downLabel === '-';
  }

  public get downIsNotMinus(): boolean {
    return Boolean(this.downLabel) && this.downLabel !== '-';
  }

  private get defaultVal(): string {
    return this.defaultValue || this.valuesList[0];
  }

  public ngOnInit(): void {
    this.initValue = !!this.val && this.val !== '0';
    this.questionEventsService.hideAllInputForManip.next(true);

    this.valuesList = this.values
      ? this.values.split(',')
      : range(this.minValue, this.maxValue + 1).map(val => val.toString());
    this.updateItemIndex(this.val);
    this.questionEventsService.questionClosed
      .pipe(
        tap((eventInfo: { byTeacher?: boolean }) => {
          this.questionClosed = true;
          // remove glow animation
          if (this.add?.nativeElement) {
            (this.add.nativeElement as HTMLElement).classList.remove('is-active');
          }
          if (this.substract?.nativeElement) {
            (this.substract.nativeElement as HTMLElement).classList.remove('is-active');
          }
          if (this.reverse?.nativeElement) {
            (this.reverse.nativeElement as HTMLElement).classList.remove('is-active');
          }
          if (eventInfo?.byTeacher) {
            this.isClosedByTeacher = true;
          }
        }),
        untilDestroyed(this)
      )
      .subscribe();

    if (!this.val) {
      this.val = this.defaultVal;
      this.valueChange.emit(this.defaultVal);
    }
  }

  public ngAfterViewInit(): void {
    // Glow animation when the secondary buttons are not engaged for more than 5 seconds,
    setTimeout(() => {
      if (!this.isManipChanged && !this.questionClosed) {
        if (this.add?.nativeElement) {
          (this.add.nativeElement as HTMLElement).classList.add('is-active');
        }
        if (this.substract?.nativeElement) {
          (this.substract.nativeElement as HTMLElement).classList.add('is-active');
        }
        if (this.reverse?.nativeElement) {
          (this.reverse.nativeElement as HTMLElement).classList.add('is-active');
        }
      }
    }, 5000);
  }

  public updateItemIndex(value?: string): void {
    this.itemIndex = 0;
    for (let i = 0; i < this.valuesList.length; i++) {
      if (this.valuesList[i] === value) {
        this.itemIndex = i;
        break;
      }
    }

    this.itemIndexChange.emit(this.itemIndex);
  }

  public showControlModal(): void {
    if (this.disabledInput || this.controlBarVisibility.isHidden()) {
      return;
    }

    const modal = this.modalService.displayModal<ManipControlsAtBottomComponent>(ManipControlsAtBottomComponent, {}, 'sm', true);
    modal.content?.closed.pipe(
      tap(() => { this.animateControlBar(); }),
      untilDestroyed(this)
    )
      .subscribe();
  }

  public animateControlBar(): void {
    //  TODO: need to use AngularX way @ViewChild
    let StudentControlBars = document.getElementsByClassName('ManipIntegerClickerBar');

    for (let i = 0; i < StudentControlBars.length; i++) {
      if (StudentControlBars[i].classList) {
        StudentControlBars[i].classList.add('animated');
        StudentControlBars[i].classList.add('bounceInDown');
      } else {
        StudentControlBars[i].className += ' animated bounceInDown';
      }
    }
    //  TODO: need to use AngularX way @ViewChild
    StudentControlBars = document.getElementsByClassName('ManipIntegerClickerBar');

    // Remove animated classes for re-use
    setTimeout(() => {
      const className = 'animated bounceInDown'; // previously "shake"
      for (let i = 0; i < StudentControlBars.length; i++) {
        if (StudentControlBars[i].classList) {
          StudentControlBars[i].classList.remove('animated');
          StudentControlBars[i].classList.remove('bounceInDown');
        } else {
          StudentControlBars[i].className =
            StudentControlBars[i].className.replace(new RegExp('(^|\\b)' + className.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
        }
      }
    }, 1000);
  }

  public addValue(change: number): void {
    this.isManipChanged = true;
    // remove glow animation
    if (this.add?.nativeElement instanceof HTMLElement) {
      this.add.nativeElement.classList.remove('is-active');
    }
    if (this.substract?.nativeElement instanceof HTMLElement) {
      this.substract.nativeElement.classList.remove('is-active');
    }
    if (this.reverse?.nativeElement instanceof HTMLElement) {
      this.reverse.nativeElement.classList.remove('is-active');
    }
    // HACK: Temporary solution. Should be refactored to receive component mode (isPreview: true/false) as an input.
    const musicService = this.route.snapshot.url.find(segment => segment.path === 'student')
      ? this.studentMusicService
      : this.sharedMusicService;

    const newIndex = this.itemIndex + change;
    if (newIndex < 0 || newIndex >= this.valuesList.length) {
      musicService.playSound('bop.mp3');

      return;
    }

    musicService.playSound('pop.mp3');
    this.valueChange.emit(this.valuesList[newIndex]);
  }

  public submit(value: string): void {
    this.submitAnswer.emit(value);
  }
}
